import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  DrawingShapeElements,
  PushedUserDesignOperation,
  UndoStackState,
} from './undoStack.slice.types';

import { UserDesignOperation } from '~/core/enums';

const initialUndoStackState: UndoStackState = {
  undoIndex: undefined,
  maxUndoIndex: 0,
  userDesignOperationsData: {
    latestPushedOperation: undefined,
    latestBeforeUndoRedo: undefined,
    selectedOperations: [],
  },
  drawingShapeElementsStack: [],
};

const undoStackSlice = createSlice({
  name: 'undoStack',
  initialState: initialUndoStackState,
  reducers: {
    // Updates the full state
    clearUndoStackState: (state) => {
      Object.assign(state, initialUndoStackState);
    },
    updateUndoIndex: (state, action: PayloadAction<number | undefined>) => {
      state.undoIndex = action.payload;
      state.maxUndoIndex = Math.max(state.undoIndex ?? 0, state.maxUndoIndex);
    },
    pushOperationUndoStack: (
      state,
      action: PayloadAction<DrawingShapeElements>
    ) => {
      state.drawingShapeElementsStack = [
        ...state.drawingShapeElementsStack.slice(0, state.undoIndex),
        action.payload,
      ];

      state.maxUndoIndex = Math.max(
        state.drawingShapeElementsStack.length - 1,
        0
      );
    },
    addUserDesignOperation: (
      state,
      action: PayloadAction<UserDesignOperation>
    ) => {
      const currentOperationInList =
        state.userDesignOperationsData.selectedOperations.filter((operation) =>
          operation.startsWith(action.payload)
        );
      const [lastOperation] = currentOperationInList.slice(-1);
      const operationIndex = lastOperation
        ? parseInt(lastOperation.split('_')[1]) + 1
        : 0;
      const newOperation = `${action.payload}_${operationIndex}`;

      state.userDesignOperationsData.selectedOperations.push(newOperation);
    },
    clearUserDesignOperation: (state) => {
      state.userDesignOperationsData.selectedOperations = [];
    },
    updateLatestPushedUserDesignOperation: (
      state,
      action: PayloadAction<PushedUserDesignOperation>
    ) => {
      const { operation, isUndoRedo } = action.payload;

      state.userDesignOperationsData.latestPushedOperation = operation;
      if (!isUndoRedo) {
        state.userDesignOperationsData.latestBeforeUndoRedo = operation;
      }
    },
    updateLatestUserDesignOperationBeforeUndoRedo: (
      state,
      action: PayloadAction<string>
    ) => {
      state.userDesignOperationsData.latestBeforeUndoRedo = action.payload;
    },
  },
});

export const {
  addUserDesignOperation,
  clearUndoStackState,
  clearUserDesignOperation,
  pushOperationUndoStack,
  updateLatestPushedUserDesignOperation,
  updateLatestUserDesignOperationBeforeUndoRedo,
  updateUndoIndex,
} = undoStackSlice.actions;

export default undoStackSlice.reducer;
